import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { DownloadSimple, ArrowLeft, Target, Gauge, ShieldWarning, Path, TrendUp, Table as TableIcon } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppShell } from "@/components/AppShell";
import { Spinner } from "@/components/Spinner";
import { DisclaimerNote } from "@/components/DisclaimerNote";
import { EvidenceConfidence } from "@/components/EvidenceConfidence";
import { ScopeValidationWarnings } from "@/components/ScopeTools";
import { api, apiError, SEVERITY_STYLES, FRAMEWORK_LABELS, STATUS_LABELS } from "@/lib/api";

const label = (v) => String(v || "").replace(/_/g, " ");
const EFFORT_LABEL = { low: "Low", medium: "Medium", high: "High" };

const SEV_TONE = {
  critical: { edge: "border-l-rose-700", tint: "bg-rose-50/60" },
  high: { edge: "border-l-orange-600", tint: "bg-orange-50/60" },
  medium: { edge: "border-l-amber-600", tint: "bg-amber-50/50" },
  low: { edge: "border-l-teal-600", tint: "bg-teal-50/50" },
};

const scoreTone = (s) =>
  s >= 80
    ? { text: "text-sky-700", bar: "bg-sky-600", tint: "bg-sky-50", border: "border-sky-200" }
    : s >= 60
    ? { text: "text-teal-700", bar: "bg-teal-600", tint: "bg-teal-50", border: "border-teal-200" }
    : s >= 40
    ? { text: "text-amber-700", bar: "bg-amber-600", tint: "bg-amber-50", border: "border-amber-200" }
    : { text: "text-rose-700", bar: "bg-rose-700", tint: "bg-rose-50", border: "border-rose-200" };

const ScoreBar = ({ score }) => {
  const tone = scoreTone(score);
  return (
    <div className="flex min-w-[132px] items-center gap-2.5">
      <div className="h-1.5 w-20 overflow-hidden bg-slate-200">
        <div className={`h-full ${tone.bar}`} style={{ width: `${Math.max(2, score)}%` }} />
      </div>
      <span className={`font-display text-sm font-bold ${tone.text}`}>{score}%</span>
    </div>
  );
};

const SectionTitle = ({ n, icon: Icon, children }) => (
  <div className="flex items-center gap-3 border-b-2 border-navy-800/15 pb-2">
    {Icon && <Icon size={20} weight="duotone" className="text-navy-800" />}
    <h2 className="font-display text-xl font-bold text-navy-900">
      <span className="mr-2 font-mono text-sm text-navy-700/60">{n}</span>
      {children}
    </h2>
  </div>
);

export default function Report({ admin = false }) {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const base = admin ? `/admin/assessments/${assessmentId}/report` : `/reports/${assessmentId}`;

  useEffect(() => {
    api
      .get(base)
      .then(({ data: d }) => setData(d))
      .catch((e) => {
        toast.error(apiError(e));
        navigate(admin ? "/admin" : `/results/${assessmentId}`);
      });
  }, [assessmentId, navigate, admin, base]);

  const downloadFile = async (kind) => {
    setDownloading(kind);
    try {
      const path = admin ? `${base}/${kind}` : `/reports/${assessmentId}/${kind}`;
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        kind === "csv"
          ? `remediation-tracker-${data.purchase_id}.csv`
          : `readiness-report-${data.purchase_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(null);
    }
  };

  if (!data)
    return (
      <AppShell breadcrumb="report">
        <Spinner label="Loading your report" />
      </AppShell>
    );

  const r = data.results;
  const scope = r.scope;
  const ctx = r.severity_context || {};

  return (
    <AppShell breadcrumb={admin ? `admin / report / ${data.product.name}` : `report / ${data.product.name}`}>
      <div className="mx-auto max-w-4xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(admin ? "/admin" : `/results/${assessmentId}`)}
          data-testid="back-to-results-button"
        >
          <ArrowLeft size={15} className="mr-1.5" /> {admin ? "Back to admin" : "Back to results"}
        </Button>

        {admin && data.admin_context && (
          <div
            className="mt-3 border border-navy-800/30 bg-navy-900/5 p-4 text-xs leading-relaxed text-navy-900"
            data-testid="admin-report-context"
          >
            <span className="font-semibold">Admin view.</span> Account {data.admin_context.user_email} ·
            assessment {data.admin_context.assessment_status.replace(/_/g, " ")} ·{" "}
            {data.admin_context.answered}/{data.admin_context.total} answered ·{" "}
            {data.admin_context.purchased
              ? "showing the immutable purchased snapshot."
              : "not purchased, showing a live computation from the answers recorded so far."}
          </div>
        )}

        <div className="mt-4 border border-slate-200 bg-white p-8 lg:p-12" data-testid="paid-report">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-6">
            <div>
              <p className="overline text-navy-800">Indicative readiness report</p>
              <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-900">{data.product.name}</h1>
              <p className="mt-2 font-mono text-[11px] text-slate-500" data-testid="report-reference">
                Ref {data.purchase_id} · {new Date(data.purchased_at).toLocaleString()} · question set v
                {r.content_version} · scoring v{r.scoring_logic_version} · mappings v{r.mapping_version}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => downloadFile("csv")}
                disabled={!!downloading}
                data-testid="download-csv-button"
              >
                <TableIcon size={16} className="mr-2" />
                {downloading === "csv" ? "Preparing…" : "Download CSV tracker"}
              </Button>
              <Button
                onClick={() => downloadFile("pdf")}
                disabled={!!downloading}
                className="bg-navy-800 text-white hover:bg-navy-700"
                data-testid="download-pdf-button"
              >
                <DownloadSimple size={16} className="mr-2" />
                {downloading === "pdf" ? "Preparing…" : "Download PDF"}
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            The CSV lists every finding with severity, roadmap window, expected evidence and
            remediation, plus empty Owner, Status, Target date, Evidence link and Progress notes
            columns so you can run it as your remediation tracker.
          </p>

          {/* 1. Assessment scope */}
          {scope && (
            <section className="mt-9" data-testid="assessment-scope">
              <SectionTitle n="01" icon={Target}>Assessment scope</SectionTitle>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                What was assessed, and on what basis, stated before any score.
              </p>
              <div className="mt-5 overflow-hidden border border-slate-200">
                <dl className="divide-y divide-slate-200 text-sm">
                  {[
                    ["Product assessed", scope.product_name],
                    ["Description", scope.product_description],
                    ["Product version", scope.product_version],
                    ["Firmware version", scope.firmware_version],
                    ["Product category", scope.category_label],
                    ["Deployment model", label(scope.deployment_model)],
                    ["Connectivity", scope.connectivity_label],
                    ["External interfaces", scope.interfaces.length ? scope.interfaces.map(label).join(", ") : "None declared"],
                    ["Declared capabilities", scope.capabilities.length ? scope.capabilities.map(label).join(", ") : "None declared"],
                    ["Target markets", scope.markets.join(", ") || "None declared"],
                    ["EU market applicability", scope.eu_market_applicability],
                    ["Basis of assessment", scope.basis],
                  ].map(([k, v]) => (
                    <div key={k} className="grid gap-1 px-4 py-3 sm:grid-cols-[190px_1fr]">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{k}</dt>
                      <dd className="text-slate-700" data-testid={`scope-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <h3 className="mt-7 font-display text-base font-bold text-navy-900">
                Frameworks considered, and why
              </h3>
              <div className="mt-3 space-y-2" data-testid="frameworks-considered">
                {scope.frameworks_considered.map((f) => (
                  <div
                    key={f.key}
                    className="border-l-2 bg-slate-50/60 p-4"
                    style={{ borderColor: f.status === "applicable" ? "#1E3A8A" : f.status === "limited" ? "#B45309" : "#CBD5E1" }}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-sm font-bold text-navy-900">{f.name}</p>
                      <span className="font-mono text-[10px] uppercase tracking-wide text-slate-500">
                        {STATUS_LABELS[f.status]}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{f.rationale}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">{scope.interfaces_note}</p>
              {r.scope_validation?.length > 0 && (
                <div className="mt-5">
                  <ScopeValidationWarnings warnings={r.scope_validation} testId="report-scope-warnings" />
                </div>
              )}
            </section>
          )}

          {/* 2. Progress over time */}
          <section className="mt-12" data-testid="progress-section">
            <SectionTitle n="02" icon={TrendUp}>Progress over time</SectionTitle>
            {(data.history || []).length < 2 ? (
              <p className="mt-4 border-l-[3px] border-l-teal-600 bg-teal-50/50 p-4 text-sm leading-relaxed text-slate-700">
                This is the first completed assessment for this product, so there is no trend yet.
                Reassess after remediation and this section will chart how readiness, evidence
                confidence and open findings move.
              </p>
            ) : (
              <>
                <p className="mt-3 text-sm leading-relaxed text-slate-700" data-testid="progress-summary">
                  {data.history.length} completed assessments. Average readiness moved from{" "}
                  <span className="font-semibold">{data.history[0].overall_score}%</span> to{" "}
                  <span className={`font-semibold ${scoreTone(data.history[data.history.length - 1].overall_score).text}`}>
                    {data.history[data.history.length - 1].overall_score}%
                  </span>
                  , open findings from {data.history[0].findings_count} to{" "}
                  {data.history[data.history.length - 1].findings_count}, evidence confidence from{" "}
                  {data.history[0].evidence_confidence} to{" "}
                  {data.history[data.history.length - 1].evidence_confidence}.
                </p>
                <div className="mt-5 h-64 w-full border border-slate-200 bg-slate-50/60 p-4" data-testid="progress-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={data.history.map((h) => ({
                        date: h.at.slice(0, 10),
                        readiness: h.overall_score,
                        evidence: h.evidence_coverage_pct,
                      }))}
                      margin={{ top: 8, right: 12, bottom: 4, left: -18 }}
                    >
                      <CartesianGrid stroke="#cbd5e1" strokeDasharray="2 4" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#475569" }} stroke="#cbd5e1" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#475569" }} stroke="#cbd5e1" />
                      <Tooltip formatter={(v, n) => [`${v}%`, n === "readiness" ? "Readiness" : "Evidence coverage"]} />
                      <Line type="monotone" dataKey="readiness" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 4, fill: "#1E3A8A" }} />
                      <Line type="monotone" dataKey="evidence" stroke="#0F766E" strokeWidth={1.8} strokeDasharray="4 3" dot={{ r: 3, fill: "#0F766E" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Solid navy: average readiness across applicable frameworks. Dashed teal: evidence
                  coverage.
                </p>
                <div className="mt-5 overflow-x-auto">
                  <Table data-testid="progress-table">
                    <TableHeader>
                      <TableRow className="bg-navy-900">
                        <TableHead className="text-white">Framework</TableHead>
                        <TableHead className="text-white">First assessment</TableHead>
                        <TableHead className="text-white">Latest</TableHead>
                        <TableHead className="text-white">Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(data.history[data.history.length - 1].framework_scores || {}).map(
                        ([k, latest], i) => {
                          const first = data.history[0].framework_scores?.[k];
                          const diff = first == null ? null : Math.round((latest - first) * 10) / 10;
                          return (
                            <TableRow key={k} className={i % 2 ? "bg-slate-50/70" : undefined}>
                              <TableCell className="font-mono text-xs">{FRAMEWORK_LABELS[k] || k}</TableCell>
                              <TableCell className="text-xs">{first == null ? "-" : `${first}%`}</TableCell>
                              <TableCell className="text-xs font-semibold">{latest}%</TableCell>
                              <TableCell
                                className={`text-xs font-semibold ${
                                  diff == null ? "text-slate-500" : diff > 0 ? "text-teal-700" : diff < 0 ? "text-rose-700" : "text-slate-500"
                                }`}
                              >
                                {diff == null ? "newly in scope" : `${diff > 0 ? "+" : ""}${diff} pts`}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </section>

          {/* 3. Executive summary */}
          <section className="mt-12">
            <SectionTitle n="03">Executive summary</SectionTitle>
            <p className="mt-3 text-sm leading-relaxed text-slate-700" data-testid="executive-summary">
              {data.executive_summary}
            </p>
          </section>

          {/* 3. Top 5 actions */}
          <section className="mt-12" data-testid="top-actions">
            <SectionTitle n="04" icon={Path}>Top 5 actions to become more ready</SectionTitle>
            <div className="mt-5 space-y-3">
              {(r.top_actions || []).map((a) => (
                <div key={a.question_code} className={`border border-slate-200 border-l-[3px] ${SEV_TONE[a.severity]?.edge || "border-l-navy-800"} ${SEV_TONE[a.severity]?.tint || ""} p-5`} data-testid={`top-action-${a.rank}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <h3 className="font-display text-base font-bold text-navy-900">
                      <span className="mr-2 font-mono text-sm text-navy-800">{a.rank}.</span>
                      {a.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {a.foundational && (
                        <span className="border border-navy-800/20 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-navy-800">
                          Foundational
                        </span>
                      )}
                      <span className={`border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_STYLES[a.severity]}`}>
                        {a.severity}
                      </span>
                    </div>
                  </div>
                  <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Impacts</dt>
                      <dd className="mt-0.5 text-slate-700">{a.impacts.length ? a.impacts.join(" + ") : "Assessed frameworks"}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Effort</dt>
                      <dd className="mt-0.5 text-slate-700">{EFFORT_LABEL[a.effort] || a.effort}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Priority</dt>
                      <dd className="mt-0.5 text-slate-700">{a.priority}</dd>
                    </div>
                  </dl>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    <span className="font-semibold text-navy-900">Evidence required: </span>
                    {a.evidence_required}
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{a.why}</p>
                </div>
              ))}
              {(!r.top_actions || r.top_actions.length === 0) && (
                <p className="text-sm text-slate-600">No prioritised actions: no potential gaps were identified.</p>
              )}
            </div>
          </section>

          {/* 4. Readiness and evidence confidence */}
          <section className="mt-12">
            <SectionTitle n="05" icon={Gauge}>Readiness and evidence confidence</SectionTitle>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="border border-slate-200 p-5">
                <p className="overline text-slate-500">Frameworks in scope</p>
                <p className="mt-2 font-display text-3xl font-extrabold text-navy-900">
                  {Object.values(r.framework_scores).filter((v) => v.display_score).length}
                  <span className="text-lg text-slate-400"> / {Object.keys(r.framework_scores).length}</span>
                </p>
                <p className="mt-1 text-xs text-slate-500">Scored for this product profile</p>
              </div>
              <EvidenceConfidence
                level={r.evidence_confidence}
                coverage={r.evidence_coverage_pct}
                unknownCount={r.uncertainty_count}
              />
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="framework-score-cards">
              {Object.entries(r.framework_scores)
                .filter(([, v]) => v.display_score)
                .map(([k, v]) => {
                  const tone = scoreTone(v.score);
                  return (
                    <div
                      key={k}
                      className={`border ${tone.border} ${tone.tint} p-4`}
                      data-testid={`framework-card-${k}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-mono text-[11px] uppercase tracking-wide text-slate-600">
                          {FRAMEWORK_LABELS[k]}
                        </p>
                        <span className={`font-display text-2xl font-extrabold ${tone.text}`}>
                          {v.score}%
                        </span>
                      </div>
                      <div className="mt-3 h-1.5 w-full overflow-hidden bg-white/70">
                        <div className={`h-full ${tone.bar}`} style={{ width: `${Math.max(2, v.score)}%` }} />
                      </div>
                      <p className="mt-2 text-[11px] text-slate-600">
                        {v.risk_band} · evidence {v.evidence_confidence} ({v.evidence_coverage_pct}%)
                      </p>
                    </div>
                  );
                })}
            </div>

            <div className="mt-5 overflow-x-auto">
              <Table data-testid="framework-table">
                <TableHeader>
                  <TableRow className="bg-navy-900">
                    <TableHead className="text-white">Framework</TableHead>
                    <TableHead className="text-white">Readiness</TableHead>
                    <TableHead className="text-white">Band</TableHead>
                    <TableHead className="text-white">Evidence confidence</TableHead>
                    <TableHead className="text-white">Applicability</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(r.framework_scores).map(([k, v], i) => (
                    <TableRow key={k} className={i % 2 ? "bg-slate-50/70" : undefined}>
                      <TableCell className="font-mono text-xs">{FRAMEWORK_LABELS[k]}</TableCell>
                      <TableCell>
                        {v.display_score ? <ScoreBar score={v.score} /> : <span className="text-xs">-</span>}
                      </TableCell>
                      <TableCell className={`text-xs font-semibold ${v.display_score ? scoreTone(v.score).text : ""}`}>
                        {v.display_score ? v.risk_band : "-"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {v.evidence_confidence} ({v.evidence_coverage_pct}%)
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{STATUS_LABELS[v.status]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <h3 className="mt-8 font-display text-base font-bold text-navy-900">
              How this score is calculated
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Yes = 2 points, Partially = 1, No = 0, Don't Know = 0. Not Applicable answers are removed
              from both the achieved and the possible points, which is why a framework's denominator
              differs from its question count.
            </p>
            <div className="mt-4 space-y-3" data-testid="score-calculations">
              {Object.entries(r.framework_scores)
                .filter(([, v]) => v.display_score)
                .map(([k, v]) => (
                  <div key={k} className="border border-slate-200 bg-slate-50/60 p-4" data-testid={`score-calc-${k}`}>
                    <p className="font-display text-sm font-bold text-navy-900">
                      {FRAMEWORK_LABELS[k]}, {v.score}%
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{v.calculation_note}</p>
                    <p className="mt-2 font-mono text-[11px] text-slate-500">
                      Yes {v.answer_breakdown?.yes ?? 0} · Partially {v.answer_breakdown?.partially ?? 0} · No{" "}
                      {v.answer_breakdown?.no ?? 0} · Don't Know {v.uncertainty_count} (uncertainty) · N/A excluded{" "}
                      {v.excluded_na} · points {v.points_earned}/{v.points_possible}
                    </p>
                  </div>
                ))}
            </div>

            <h3 className="mt-8 font-display text-base font-bold text-navy-900">How severity is derived</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="severity-model">
              Severity reflects requirement importance combined with this product's context, exposure
              ({ctx.exposure_reason}) and consequence ({ctx.consequence_reason}), and how completely each control
              is in place. Each finding states its own drivers; the underlying arithmetic is in{" "}
              <span className="font-semibold">Appendix A</span>.
            </p>
          </section>

          {/* 5. Findings register */}
          <section className="mt-12">
            <SectionTitle n="06" icon={ShieldWarning}>Findings register</SectionTitle>
            <p className="mt-1 text-xs text-slate-500">
              {r.findings.length} potential gaps, ordered by contextual risk score
            </p>
            <div className="mt-5 space-y-5" data-testid="findings-register">
              {r.findings.map((f, i) => (
                <div
                  key={f.question_code}
                  className={`border border-slate-200 border-l-[3px] ${SEV_TONE[f.severity]?.edge || "border-l-slate-300"} ${SEV_TONE[f.severity]?.tint || ""} p-5`}
                  data-testid={`finding-${f.question_code}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[11px] text-slate-500">{String(i + 1).padStart(2, "0")}</span>
                    <span className={`border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_STYLES[f.severity]}`}>
                      {f.severity}
                    </span>
                    <span className="font-mono text-[11px] text-slate-500">{f.question_code}</span>
                    <span className="text-[11px] text-slate-500">{f.section_name}</span>
                    <span className="text-[11px] text-slate-500">· Response: {f.answer_label}</span>
                    {f.foundational && (
                      <span className="border border-navy-800/20 bg-blue-50 px-2 py-0.5 text-[10px] uppercase tracking-wide text-navy-800">
                        Foundational
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 font-display text-base font-bold text-navy-900">{f.title}</h3>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
                    <p><span className="font-semibold text-navy-900">Cross-framework impact: </span>{f.cross_framework_impact}</p>
                    <p><span className="font-semibold text-navy-900">Expected evidence: </span>{f.expected_evidence}</p>
                    <p>
                      <span className="font-semibold text-navy-900">Remediation action: </span>
                      {f.remediation} <span className="text-xs text-slate-500">(effort: {EFFORT_LABEL[f.effort] || f.effort})</span>
                    </p>
                    {f.severity_inputs && (
                      <p className="text-xs leading-relaxed text-slate-600" data-testid={`severity-reason-${f.question_code}`}>
                        <span className="font-semibold text-navy-900">Severity: {f.severity[0].toUpperCase() + f.severity.slice(1)}</span>
                        {", "}
                        {f.severity_reason ||
                          "driven by this product's exposure and consequence"}
                        <span className="text-slate-400"> (arithmetic in Appendix A)</span>
                      </p>
                    )}
                    {f.uncertainty && (
                      <p className="border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                        Validation risk: answered Don't Know. This lowers evidence confidence rather than
                        the readiness score, and must be verified internally.
                      </p>
                    )}
                    {f.user_comment && <p className="text-xs text-slate-500">Your note: {f.user_comment}</p>}
                  </div>
                </div>
              ))}
              {r.findings.length === 0 && (
                <p className="text-sm text-slate-600">No potential gaps were identified from your responses.</p>
              )}
            </div>
          </section>

          {/* 6. Roadmap */}
          <section className="mt-12">
            <SectionTitle n="07">Sequenced 30 / 60 / 90 day roadmap</SectionTitle>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Sequencing is not severity sorting alone: foundational work is pulled forward where later
              technical remediation depends on it.
            </p>
            <div className="mt-6 space-y-8" data-testid="roadmap">
              {r.roadmap.map((w) => (
                <div key={w.window} className="relative border-l-2 border-navy-800 pl-6" data-testid={`roadmap-${w.window.replace(/[^0-9a-z]/gi, "-")}`}>
                  <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-navy-800" />
                  <p className="inline-block bg-navy-900 px-3 py-1 font-display text-sm font-bold text-white">
                    {w.window} · {w.total_items} items
                  </p>
                  <p className="mt-1 text-xs text-slate-600">{w.objective}</p>
                  <ul className="mt-4 space-y-3.5">
                    {w.actions.map((a) => (
                      <li key={a.question_code} className="text-sm leading-relaxed text-slate-700">
                        <span className={`mr-2 border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${SEVERITY_STYLES[a.severity]}`}>
                          {a.severity}
                        </span>
                        <span className="font-semibold text-navy-900">{a.action_title}</span>, {a.action}
                        <span className="mt-1 block text-xs text-slate-500">
                          {a.section_name} · effort {EFFORT_LABEL[a.effort] || a.effort}
                          {a.foundational ? " · foundational" : ""}
                          {a.standing ? " · scope & programme action" : ""}
                        </span>
                        <span className="mt-0.5 block text-xs italic text-slate-500" data-testid={`sequencing-note-${a.question_code}`}>
                          {String(a.sequencing_note || "").toLowerCase().startsWith("no prerequisite")
                            ? ""
                            : a.sequencing_note}
                        </span>
                      </li>
                    ))}
                    {w.actions.length === 0 && <li className="text-sm text-slate-500">No items in this window.</li>}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <SectionTitle n="08">Strengths observed</SectionTitle>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {r.strengths.map((s) => (
                <li key={s.question_code}>• {s.title} <span className="text-xs text-slate-500">({s.section_name})</span></li>
              ))}
              {r.strengths.length === 0 && <li className="text-slate-500">No fully affirmed areas were reported.</li>}
            </ul>
          </section>

          <section className="mt-12">
            <SectionTitle n="A">Appendix A, how severity is calculated</SectionTitle>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Deterministic model:{" "}
              <span className="font-mono text-xs">
                risk = requirement importance × answer quality × product context
              </span>
              , where product context ={" "}
              <span className="font-mono text-xs">1 + 0.5 × ((exposure − 1) + (consequence − 1))</span>. Importance:
              critical 4.0, high 3.0, medium 2.0, low 1.0. Answer quality: No 1.0, Don't Know 0.8, Partially 0.55.
              Bands: risk ≥ 6.0 critical, ≥ 3.6 high, ≥ 1.8 medium, otherwise low. For this product exposure is{" "}
              {ctx.exposure_factor} and consequence is {ctx.consequence_factor}, giving a context multiplier of{" "}
              {ctx.context_factor}.
            </p>
            <div className="mt-4 overflow-x-auto">
              <Table data-testid="severity-appendix">
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref</TableHead>
                    <TableHead>Importance</TableHead>
                    <TableHead>Answer quality</TableHead>
                    <TableHead>Context</TableHead>
                    <TableHead>Risk</TableHead>
                    <TableHead>Severity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {r.findings.map((f) => (
                    <TableRow key={f.question_code}>
                      <TableCell className="font-mono text-[11px]">{f.question_code}</TableCell>
                      <TableCell className="font-mono text-[11px]">{f.severity_inputs?.requirement_importance ?? "-"}</TableCell>
                      <TableCell className="font-mono text-[11px]">{f.severity_inputs?.answer_quality ?? "-"}</TableCell>
                      <TableCell className="font-mono text-[11px]">{f.severity_inputs?.context_factor ?? ctx.context_factor ?? "-"}</TableCell>
                      <TableCell className="font-mono text-[11px]">{f.risk_score ?? "-"}</TableCell>
                      <TableCell className="text-[11px] capitalize">{f.severity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          <div className="mt-12">
            <DisclaimerNote text={data.disclaimer} testId="report-disclaimer" />
          </div>
        </div>
      </div>
    </AppShell>
  );
}
